import React, { useEffect, useState } from 'react';
import {
  Wine,
  CalendarBlank,
  MapPin,
  Cake,
  MusicNotes,
  Heart,
  Confetti,
  Camera,
  Phone,
  UsersThree,
  IceCream,
  PawPrint,
  Bone,
  Cookie,
  Clock,
  CaretLeft,
  CaretRight,
  X,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { FeatureChipScroller, FeatureListGrid } from './FeatureTiles';

const amenityTiles = [
  { icon: CalendarBlank, label: 'Open daily', sub: '10 AM – 10 PM' },
  { icon: MapPin, label: 'Next to the play area', sub: 'Walk in after a session' },
  { icon: IceCream, label: 'Doggie ice-cream bar', sub: 'Pup-safe scoops on weekends' },
  { icon: Wine, label: "Pawrents' lounge", sub: 'Wine, coffee & slow evenings' },
  { icon: MusicNotes, label: 'Live acoustic nights', sub: 'Every Friday' },
  { icon: Heart, label: 'Pet-safe menu', sub: 'Vet-reviewed treats' },
];

const birthdayInclusions = [
  { Icon: Cake, label: 'Custom pet-safe cake' },
  { Icon: Confetti, label: 'Themed décor & balloons' },
  { Icon: UsersThree, label: 'Family & friends welcome' },
];

const BirthdaySpotlight = () => (
  <div className="mt-5 overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 sm:p-10">
    <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_1fr]">
      <div className="relative">
        <div className="overflow-hidden rounded-2xl">
          <img
            src="https://images.unsplash.com/photo-1668302776137-bda9a1628c4f?auto=format&fit=crop&w=1400&q=80"
            alt="Dog wearing a birthday hat celebrating its birthday"
            className="h-72 w-full object-cover lg:h-96"
          />
        </div>
        <span className="absolute -top-3 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#d54a30] px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
          <Cake size={14} weight="fill" />
          Custom cake
        </span>
        <div className="absolute -bottom-4 right-4 hidden items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-xl sm:flex">
          <Confetti size={18} weight="duotone" className="text-[#d54a30]" />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#d54a30]">Hosted</p>
            <p className="text-sm font-semibold text-neutral-900">400+ pawties</p>
          </div>
        </div>
      </div>

      <div>
        <span className="inline-flex items-center gap-2 rounded-full bg-[#d54a30]/10 px-3 py-1 text-xs font-semibold text-[#9a3220]">
          <Confetti size={13} weight="fill" />
          Birthday Hostings
        </span>
        <h3 className="mt-4 text-xl font-semibold leading-tight text-neutral-900 sm:text-2xl lg:text-3xl">
          Throw them a pawty they&apos;ll never forget.
        </h3>
        <p className="mt-4 text-base leading-relaxed text-neutral-600">
          Custom cakes, themed décor, a private play session and a photo album — all set up steps from the café.
        </p>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {birthdayInclusions.map(({ Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-2 rounded-lg bg-neutral-50 p-4 text-center">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white">
                <Icon size={18} weight="duotone" className="text-[#d54a30]" />
              </div>
              <p className="text-sm font-semibold leading-tight text-neutral-900">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href="tel:+918861712345"
            className="inline-flex items-center gap-3 rounded-full bg-[#d54a30] px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#b73e26]"
          >
            <Phone size={18} weight="fill" />
            Book Now
          </a>
          <a
            href="https://wa.me/918861712345?text=Hi%20Petcart%2C%20I%27d%20like%20to%20book%20a%20birthday%20pawty%20at%20Wag%20%26%20Wine."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 rounded-full border border-[#25D366]/30 bg-white px-5 py-3 text-sm font-bold text-[#25D366] shadow-sm transition hover:border-[#25D366]/50 hover:bg-[#25D366]/15"
          >
            <span
              aria-hidden="true"
              className="h-[18px] w-[18px] bg-current"
              style={{
                WebkitMaskImage: 'url(/images/whatsapp.svg)',
                maskImage: 'url(/images/whatsapp.svg)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
              }}
            />
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  </div>
);

const upcomingEvents = [
  {
    title: 'Wag & Wine Evening',
    date: 'Dec 20, 2025',
    time: '6:00 PM',
    location: 'Main Lounge',
    image: 'https://images.unsplash.com/photo-1597046835715-16f81ac132c0?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
  {
    title: 'Holiday Pet Party',
    date: 'Dec 22, 2025',
    time: '4:00 PM',
    location: 'Dog Cafe',
    image: 'https://images.unsplash.com/photo-1766114314882-89b64589f2c5?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
];

const EventsBlock = () => (
  <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-6 sm:p-10">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full bg-[#d54a30]/10 px-3 py-1 text-xs font-semibold text-[#9a3220]">
          <CalendarBlank size={13} weight="fill" />
          Wag & Wine Events
        </span>
        <h3 className="mt-3 text-2xl font-semibold leading-tight text-neutral-900 sm:text-3xl">
          What&apos;s happening at the café
        </h3>
      </div>
      <Button variant="outline" className="h-9 rounded-md px-5 text-xs font-bold">
        View all events
      </Button>
    </div>

    <div className="mt-8 grid gap-5 md:grid-cols-2">
      {upcomingEvents.map((event) => (
        <article key={event.title} className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
          <div className="relative h-44">
            <img src={event.image} alt={event.title} className="h-full w-full object-cover" />
            <span className="absolute left-3 top-3 rounded-full bg-white px-2 py-1 landing-badge text-neutral-700">Event</span>
          </div>
          <div className="p-5">
            <div className="landing-card-meta mb-2 flex items-center gap-2">
              <CalendarBlank size={13} className="text-primary" weight="duotone" />
              {event.date}
            </div>
            <h4 className="landing-card-label">{event.title}</h4>
            <div className="landing-card-meta mt-3 flex flex-wrap gap-4">
              <span className="flex items-center gap-1"><Clock size={12} weight="duotone" />{event.time}</span>
              <span className="flex items-center gap-1"><MapPin size={12} weight="duotone" />{event.location}</span>
            </div>
            <Button className="mt-5 h-9 rounded-md bg-primary px-4 text-xs font-bold text-white hover:bg-primary/90">
              RSVP Now
            </Button>
          </div>
        </article>
      ))}
    </div>
  </div>
);

const galleryImages = [
  'WhatsApp Image 2026-05-13 at 8.29.27 PM.jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (1).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (2).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (4).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (5).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (6).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (7).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (8).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (9).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (10).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.27 PM (12).jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.28 PM.jpeg',
  'WhatsApp Image 2026-05-13 at 8.29.28 PM (1).jpeg',
].map((name) => `/images/wag-wine-gallery/${encodeURIComponent(name)}`);

const bentoTiles = [
  { idx: 0, span: 'sm:col-span-2 sm:row-span-2' },
  { idx: 1, span: '' },
  { idx: 2, span: '' },
  { idx: 3, span: '' },
  { idx: 4, span: '' },
  { idx: 5, span: 'sm:col-span-2' },
  { idx: 6, span: '' },
  { idx: 7, span: '' },
  { idx: 8, span: '' },
  { idx: 9, span: '' },
  { idx: 10, span: 'sm:col-span-2 sm:row-span-2' },
  { idx: 11, span: '' },
  { idx: 12, span: '' },
];

const Gallery = () => {
  const total = galleryImages.length;
  const [step, setStep] = useState(null);

  const close = () => setStep(null);
  const prev = () => setStep((s) => (s === null ? null : (s - 1 + total) % total));
  const next = () => setStep((s) => (s === null ? null : (s + 1) % total));

  useEffect(() => {
    if (step === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step === null) return;
    const t = setTimeout(() => {
      setStep((s) => (s === null ? null : (s + 1) % total));
    }, 3500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <div className="mt-12">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#d54a30]/10 px-3 py-1 text-xs font-semibold text-[#9a3220]">
            <Camera size={13} weight="fill" />
            Gallery
          </span>
          <h3 className="mt-3 text-2xl font-semibold leading-tight text-neutral-900 sm:text-3xl">
            Moments from the café
          </h3>
        </div>
      </div>
      <div className="grid auto-rows-[140px] grid-cols-2 gap-3 sm:auto-rows-[160px] sm:grid-cols-4 lg:auto-rows-[180px] lg:gap-4">
        {bentoTiles.map(({ idx, span }) => (
          <button
            type="button"
            key={galleryImages[idx]}
            onClick={() => setStep(idx)}
            className={`group overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100 text-left ${span}`}
          >
            <img
              src={galleryImages[idx]}
              alt={`Wag and Wine moment ${idx + 1}`}
              loading="lazy"
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
            />
          </button>
        ))}
      </div>

      <Dialog open={step !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="!flex h-[72vh] max-h-[680px] w-[95vw] max-w-5xl !gap-0 overflow-hidden rounded-2xl border-none bg-black p-0">
          <DialogTitle className="sr-only">
            {step !== null ? `Wag and Wine moment ${step + 1}` : 'Gallery'}
          </DialogTitle>

          {step !== null && (
            <div className="relative h-full w-full">
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="absolute right-5 top-5 z-20 text-white/80 transition hover:text-white"
              >
                <X size={24} weight="bold" />
              </button>
              {galleryImages.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`Wag and Wine moment ${i + 1}`}
                  className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 ease-in-out ${i === step ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}

              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-5 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/70 text-white shadow-lg backdrop-blur-md transition hover:bg-black/85"
              >
                <CaretLeft size={20} weight="bold" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-5 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/70 text-white shadow-lg backdrop-blur-md transition hover:bg-black/85"
              >
                <CaretRight size={20} weight="bold" />
              </button>

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pb-6 pt-20">
                <div className="flex items-end justify-end gap-6 px-6 sm:px-8">
                  <div className="flex shrink-0 items-center gap-3 rounded-full border border-white/20 bg-black/40 px-3 py-1.5 shadow-md backdrop-blur-md">
                    <div className="flex items-center gap-1.5">
                      {galleryImages.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          aria-label={`Go to slide ${i + 1}`}
                          onClick={() => setStep(i)}
                          className={`h-1.5 rounded-full transition-all ${i === step ? 'w-5 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
                        />
                      ))}
                    </div>
                    <span className="text-[11px] font-medium tabular-nums text-white/80">
                      {step + 1} / {total}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const houseRules = [
  { Icon: PawPrint, label: 'All tails welcome' },
  { Icon: Bone, label: 'Leash optional inside' },
  { Icon: Cookie, label: 'Treats on the house' },
  { Icon: Wine, label: 'Wag responsibly' },
];

const HouseRulesCard = () => (
  <div className="w-[280px] rounded-2xl border border-neutral-200 bg-white p-5 shadow-2xl">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">House Rules</p>
    <div className="my-3 h-px bg-neutral-100" />
    <ul className="space-y-3">
      {houseRules.map(({ Icon, label }) => (
        <li key={label} className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Icon size={16} weight="duotone" className="text-primary" />
          </div>
          <p className="text-sm font-medium text-neutral-800">{label}</p>
        </li>
      ))}
    </ul>
    <div className="my-3 h-px bg-neutral-100" />
    <p className="text-xs italic font-medium text-neutral-500">Slow evenings, always.</p>
  </div>
);

const WagAndWine = () => {
  return (
    <section id="wag-and-wine" className="bg-white pt-10 pb-16 sm:pt-12 sm:pb-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="landing-section-header">
          <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">Wag &amp; Wine</span>
          <h2 className="landing-section-title">A café where every tail has a seat</h2>
          <p className="landing-section-lede mx-auto">
            Slow evenings, pet-safe plates and a pawrents' lounge — built right next to the play area.
          </p>
        </div>

        <div className="relative my-16 min-h-[460px] rounded-2xl bg-neutral-900 md:min-h-[500px]">
          <div className="absolute inset-0 overflow-hidden rounded-2xl">
            <img
              src="/images/wag-and-wine.png"
              alt="Wag and Wine café interior with pets and pet parents"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/10" />
          </div>

          <div className="relative z-10 flex min-h-[460px] flex-col items-start justify-center gap-4 p-7 md:min-h-[500px] md:p-12">
            <div className="max-w-xs text-white sm:max-w-sm">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/20 px-3 py-1 text-xs font-semibold text-white shadow-sm backdrop-blur">
                <Wine size={13} weight="fill" className="text-amber-300" />
                Powered by Wag &amp; Wine
              </span>
              <h3 className="mt-4 text-3xl font-semibold leading-tight text-white sm:text-4xl">
                Slow evenings. Wagging tails. Good company.
              </h3>
              <p className="mt-3 text-base font-medium leading-relaxed text-white/85">
                A pet-safe café right next to the play area — pup-uccinos for them, a glass of wine for you.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="tel:+918861712345"
                  className="inline-flex h-11 items-center justify-center gap-1.5 rounded-full bg-white px-6 text-sm font-semibold text-neutral-900 transition-colors hover:bg-white/90"
                >
                  <Phone size={16} weight="bold" />
                  Call to Reserve
                </a>
                <a
                  href="https://wa.me/918861712345?text=Hi%20Petcart%2C%20I%27d%20like%20to%20reserve%20a%20table%20at%20Wag%20%26%20Wine."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center justify-center gap-1.5 rounded-full border border-white/40 bg-white/10 px-6 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/20"
                >
                  <span
                    aria-hidden="true"
                    className="h-4 w-4 bg-current"
                    style={{
                      WebkitMaskImage: 'url(/images/whatsapp.svg)',
                      maskImage: 'url(/images/whatsapp.svg)',
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                    }}
                  />
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute -bottom-6 -top-6 right-6 z-20 hidden items-center lg:flex lg:right-10">
            <HouseRulesCard />
          </div>
        </div>

        <BirthdaySpotlight />

        <Gallery />

        {/* <EventsBlock /> */}

        <FeatureChipScroller
          tiles={amenityTiles}
          label="Café features"
          className="mt-5"
        />
        <FeatureListGrid tiles={amenityTiles} columns={6} className="mt-5" />
      </div>
    </section>
  );
};

export default WagAndWine;
